import { Alert, Badge, Button, Card, CardBody } from '@omnitok/ui';
import { BadgeCheck, CloudCheck, Plus, RefreshCw, Rocket, RotateCcw } from 'lucide-react';
import { ProgressBar } from '@/shared/components';
import type {
  LocalizationArtifactKind,
  LocalizationRun,
  LocalizationSseStatus,
} from '../types/localization.types';
import LocalizationItemsTable from './LocalizationItemsTable';
import LocalizationStatusChip from './LocalizationStatusChip';

interface LocalizationRunViewProps {
  run: LocalizationRun;
  sseStatus: LocalizationSseStatus;
  isApproving: boolean;
  isApplying: boolean;
  isRetryingPending: boolean;
  isRetryingPreviewFailed: boolean;
  isRetryingFailed: boolean;
  onApprove: () => void;
  onApply: () => void;
  onRetryPending: () => void;
  onRetryPreviewFailed: () => void;
  onRetryFailed: () => void;
  onRefresh: () => void;
  onNewRun: () => void;
  onDownload: (sku: string, targetLocale: string, kind: LocalizationArtifactKind) => Promise<void>;
  onSelectModel: (sku: string, modelId: string) => Promise<void>;
  isSelectingModel: boolean;
}

export default function LocalizationRunView({
  run,
  sseStatus,
  isApproving,
  isApplying,
  isRetryingPending,
  isRetryingPreviewFailed,
  isRetryingFailed,
  onApprove,
  onApply,
  onRetryPending,
  onRetryPreviewFailed,
  onRetryFailed,
  onRefresh,
  onNewRun,
  onDownload,
  onSelectModel,
  isSelectingModel,
}: LocalizationRunViewProps) {
  const targetLocales = run.targetLocales?.length
    ? run.targetLocales
    : run.targetLocale
      ? [run.targetLocale]
      : [];
  const isApplyPhase = Boolean(run.approvedAt);
  const connectionLabel = sseStatus === 'connected' ? 'SSE conectado' : 'Polling activo';
  const hasPendingApply = ['queued_apply', 'applying'].includes(run.status)
    && run.items.some((item) => item.status === 'pending');
  const hasFailedPreview = !['queued', 'processing', 'queued_apply', 'applying'].includes(run.status)
    && run.items.some((item) => item.localizations
      ? targetLocales.some((locale) => ['failed', 'invalid', 'not-found'].includes(
        item.localizations?.[locale]?.status ?? ''
      ))
      : ['failed', 'invalid', 'not-found'].includes(item.status) && !item.artifacts);
  const hasFailedApply = Boolean(run.approvedAt)
    && ['completed', 'failed'].includes(run.status)
    && run.items.some((item) => item.status === 'failed' && (item.localizations
      ? targetLocales.every((locale) => ['preview-generated', 'already-localized'].includes(
        item.localizations?.[locale]?.status ?? ''
      )) && targetLocales.some((locale) => item.localizations?.[locale]?.artifacts)
      : Boolean(item.artifacts)));

  return (
    <div className="localization-run">
      <Card noPadding>
        <CardBody>
          <div className="localization-run__header">
            <div>
              <div className="localization-inline-badges">
                <LocalizationStatusChip status={run.status} />
                <Badge
                  size="sm"
                  pill
                  variant={sseStatus === 'connected' ? 'success' : sseStatus === 'error' ? 'warning' : 'neutral'}
                >
                  {sseStatus === 'connected' && <CloudCheck size={13} aria-hidden="true" />}
                  {connectionLabel}
                </Badge>
              </div>
              <h2 className="localization-run__title">Corrida de localización</h2>
              <p className="localization-mono localization-muted">{run.runId}</p>
            </div>
            <div className="localization-actions">
              <Button variant="outline" size="sm" leftIcon={<RefreshCw size={16} />} onClick={onRefresh}>
                Actualizar
              </Button>
              <Button variant="outline" size="sm" leftIcon={<Plus size={16} />} onClick={onNewRun}>
                Nueva corrida
              </Button>
            </div>
          </div>

          <div className="localization-separator" />

          <dl className="localization-stats">
            <div><dt>Localización</dt><dd>{run.sourceLocale} → {targetLocales.join(', ')}</dd></div>
            <div><dt>Procesados</dt><dd>{run.progress.completed} de {run.progress.total}</dd></div>
            <div><dt>Exitosos</dt><dd className="localization-success">{run.progress.succeeded}</dd></div>
            <div><dt>Fallidos</dt><dd className="localization-error">{run.progress.failed}</dd></div>
          </dl>

          <ProgressBar
            value={run.progress.percentage}
            label={run.progress.currentSku
              ? `Procesando ${run.progress.currentSku}`
              : `${run.progress.completed} de ${run.progress.total} ${isApplyPhase ? 'SKU' : 'unidades'}`}
          />

          <div className="localization-notices">
            {run.error && <Alert variant="error">{run.error}</Alert>}
            {run.status === 'awaiting_selection' && (
              <Alert variant="warning">
                Uno o más SKU corresponden a varios modelos. Usa “Comparar modelos” y selecciona el modelId correcto para continuar.
              </Alert>
            )}
            {run.status === 'awaiting_review' && (
              <Alert variant="warning">
                Revisa los artefactos “Cambios” y “Preview”. Aprobar habilita la aplicación, pero todavía no modifica los modelos.
              </Alert>
            )}
            {run.status === 'approved' && (
              <Alert variant="info">
                La corrida está aprobada. Cada SKU se actualizará una sola vez y únicamente si todas sus localizaciones están listas.
              </Alert>
            )}
            {hasPendingApply && (
              <Alert variant="info">
                Si estos pendientes quedaron huérfanos después de reiniciar el backend, puedes reencolarlos manualmente.
              </Alert>
            )}
          </div>

          {(run.status === 'awaiting_review' || run.status === 'approved') && (
            <div className="localization-actions localization-actions--primary">
              {run.status === 'awaiting_review' && (
                <Button
                  variant="accept"
                  leftIcon={<BadgeCheck size={17} />}
                  loading={isApproving}
                  disabled={isApproving}
                  onClick={onApprove}
                >
                  {isApproving ? 'Aprobando…' : 'Aprobar dry-run'}
                </Button>
              )}
              {run.status === 'approved' && (
                <Button
                  variant="accent"
                  leftIcon={<Rocket size={17} />}
                  loading={isApplying}
                  disabled={isApplying}
                  onClick={onApply}
                >
                  {isApplying ? 'Encolando…' : 'Aplicar localizaciones'}
                </Button>
              )}
            </div>
          )}
          {hasPendingApply && (
            <div className="localization-actions localization-actions--primary">
              <Button
                variant="outline"
                leftIcon={<RotateCcw size={17} />}
                loading={isRetryingPending}
                disabled={isRetryingPending}
                onClick={onRetryPending}
              >
                {isRetryingPending ? 'Reencolando…' : 'Reencolar pendientes'}
              </Button>
            </div>
          )}
          {hasFailedPreview && (
            <div className="localization-actions localization-actions--primary">
              <Button
                variant="outline"
                leftIcon={<RotateCcw size={17} />}
                loading={isRetryingPreviewFailed}
                disabled={isRetryingPreviewFailed}
                onClick={onRetryPreviewFailed}
              >
                {isRetryingPreviewFailed ? 'Regenerando…' : 'Regenerar traducciones fallidas'}
              </Button>
            </div>
          )}
          {hasFailedApply && (
            <div className="localization-actions localization-actions--primary">
              <Button
                variant="outline"
                leftIcon={<RotateCcw size={17} />}
                loading={isRetryingFailed}
                disabled={isRetryingFailed}
                onClick={onRetryFailed}
              >
                {isRetryingFailed ? 'Reencolando…' : 'Reintentar fallidos'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <Card noPadding>
        <div className="localization-table-card">
          <LocalizationItemsTable
            runId={run.runId}
            targetLocales={targetLocales}
            items={run.items}
            onDownload={onDownload}
            onSelectModel={onSelectModel}
            isSelectingModel={isSelectingModel}
          />
        </div>
      </Card>
    </div>
  );
}
